import { z } from "zod"

const lexicalNodeSchema: z.ZodType<LexicalNode> = z.lazy(() =>
  z.object({
    text: z.string().optional(),
    type: z.string().optional(),
    listType: z.enum(["bullet", "number", "check"]).optional(),
    value: z.number().optional(),
    children: z.array(lexicalNodeSchema).optional(),
  }),
)

const lexicalStateSchema = z.object({
  root: lexicalNodeSchema.optional(),
})

interface LexicalNode {
  text?: string
  type?: string
  listType?: "bullet" | "number" | "check"
  /** Lexical sets this on listitem nodes — the 1-indexed position used for
   * ordered list numbering (handles `start` overrides and continued lists). */
  value?: number
  children?: LexicalNode[]
}

const BLOCK_TYPES = new Set(["paragraph", "heading", "quote"])

function inlineText(node: LexicalNode): string {
  if (typeof node.text === "string") return node.text
  if (node.type === "linebreak" || node.type === "line-break") return "\n"
  const children = node.children ?? []
  return children.map(inlineText).join("")
}

function nodeToText(node: LexicalNode): string {
  if (typeof node.text === "string") return node.text
  if (node.type === "linebreak" || node.type === "line-break") return "\n"

  if (node.type === "list") {
    const items = node.children ?? []
    const isNumbered = node.listType === "number"
    const lines: string[] = []
    items.forEach((item, index) => {
      if (item.type !== "listitem" && item.type !== "list-item") {
        lines.push(nodeToText(item).replace(/\n+$/u, ""))
        return
      }
      const itemText = inlineText(item).replace(/\s+$/u, "")
      const marker = isNumbered ? `${item.value ?? index + 1}. ` : "- "
      lines.push(`${marker}${itemText}`)
    })
    return `${lines.join("\n")}\n`
  }

  const children = node.children ?? []
  const childText = children.map(nodeToText).join("")
  if (node.type && BLOCK_TYPES.has(node.type)) {
    return `${childText}\n`
  }
  return childText
}

export function lexicalToPlainText(
  serialized: string | null | undefined,
): string {
  if (!serialized) return ""
  try {
    const result = lexicalStateSchema.safeParse(JSON.parse(serialized))
    if (!result.success || !result.data.root) return ""
    return nodeToText(result.data.root).replace(/\n+$/u, "").trim()
  } catch {
    return serialized.trim()
  }
}

export function hasLexicalContent(
  serialized: string | null | undefined,
): boolean {
  return lexicalToPlainText(serialized).length > 0
}
